import React, { FC, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { FormTextInput } from "stentor-models";

import { Styleable } from "../../types";
import { formatToInputProps } from "../../utils/formatToInputProps";
import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldTextInput.module.scss";

export interface FormTextInputProps extends FormFieldProps {
    readonly field: Styleable<FormTextInput>;
    readonly value?: string | undefined;
    /**
     * When this is the last field of the step the keyboard shows "done" rather than "next".
     */
    readonly isLastField?: boolean;
}

// Show counter when within this percentage of the limit
const COUNTER_THRESHOLD_PERCENT = 0.8;

export const FormFieldTextInput: FC<FormTextInputProps> = (props) => {
    const { field, onFieldChange, value, errorMessage, required, isLastField } = props;

    const [text, setText] = useState(value || "");
    const [focused, setFocused] = useState(false);

    const id = `xapp-text-${useId()}`;
    const helperId = `${id}-helper-text`;
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = e.target;
        setText(value);
    };

    useEffect(() => {
        onFieldChange(field.name, text);
    }, [field.name, onFieldChange, text]);

    useEffect(() => {
        setText(value || "");
    }, [value]);

    // A multiline field with no fixed `rows` grows with its content up to `rowsMax`, the way the
    // MUI textarea it replaces did. Fixed `rows` is left to the browser.
    const autosize = !!field.multiline && !field.rows;
    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!autosize || !textarea) {
            return;
        }
        textarea.style.height = "auto";
        const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight) || 0;
        const maxHeight = field.rowsMax && lineHeight ? lineHeight * field.rowsMax : Infinity;
        const next = Math.min(textarea.scrollHeight, maxHeight);
        if (next > 0) {
            textarea.style.height = `${next}px`;
        }
    }, [autosize, field.rowsMax, text]);

    const maxLength = field.maxLength;
    const currentLength = text.length;
    const isAtLimit = maxLength !== undefined && currentLength >= maxLength;
    const showCounter = maxLength !== undefined && currentLength >= maxLength * COUNTER_THRESHOLD_PERCENT;

    // field.type is the widget discriminator ("TEXT" | "DROPDOWN" | ...), not an HTML
    // input type - the real semantics live on field.format.
    const { type, ...inputHelpers } = formatToInputProps({
        format: field.format,
        name: field.name,
        isLastField,
    });

    // MUI's floating label: resting inside the field until it is focused or has a value. With no
    // label to collide with, the placeholder is always shown.
    const shrink = focused || text.length > 0 || !field.label;

    const controlProps = {
        ...inputHelpers,
        id,
        name: field.name,
        className: styles.xapp_text_input_control,
        placeholder: field.placeholder,
        value: text,
        onChange,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
        required,
        maxLength: field.maxLength || undefined,
        "aria-invalid": !!errorMessage,
        "aria-describedby": errorMessage ? helperId : undefined,
        // Read by the stylesheet, which hides the placeholder behind a resting label.
        "data-shrink": shrink ? "true" : "false",
    };

    return (
        <div className={styles.xapp_text_input_container}>
            {field.title && (
                <div className={styles["xapp_text_input_container__title"]}>
                    <p>{field.title}</p>
                </div>
            )}

            <div className={styles["xapp_text_input_container__field"]}>
                <div
                    className={`${styles.xapp_text_input_text} ${errorMessage ? styles.xapp_text_input_error : ""}`}
                    style={field.style}>
                    <div
                        className={`${styles.xapp_text_input_root} ${focused ? styles.xapp_text_input_focused : ""} ${
                            field.multiline ? styles.xapp_text_input_multiline : ""
                        }`}>
                        {field.label && (
                            <label
                                htmlFor={id}
                                className={styles.xapp_text_input_label}
                                data-shrink={shrink ? "true" : "false"}>
                                {field.label}
                                {required && (
                                    <span className={styles.xapp_text_input_asterisk} aria-hidden="true">
                                        {" *"}
                                    </span>
                                )}
                            </label>
                        )}
                        {field.multiline ? (
                            <textarea {...controlProps} ref={textareaRef} rows={field.rows || 1} />
                        ) : (
                            <input {...controlProps} type={type} />
                        )}
                    </div>
                    {errorMessage && (
                        <p id={helperId} className={styles.xapp_text_input_helper}>
                            {errorMessage}
                        </p>
                    )}
                </div>
                {showCounter && (
                    <div className={`${styles.xapp_text_input_counter} ${isAtLimit ? styles["xapp_text_input_counter--limit"] : ""}`}>
                        {currentLength}/{maxLength}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormFieldTextInput;
